import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageContainer, PageSectionTitle, Tile } from 'components/Layout/PageContainer';

import { useAnalysisData, useMessage } from 'pages/trading/queryHooks';
import { PriceChange } from 'pages/trading/model';
import { calculateDailyStats } from 'pages/trading/price/CalculateDailyStats';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { useBreadcrumbStore } from 'hooks/state/useBreadcrumbStore';
import { PriceKPICard } from './PriceKPICard';
import { getErrorMessage } from 'utils/getErrorMessage';

import { StyleDetailChart } from '../StyleDetailChart';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { TradingMessageDisplay } from '../../TradingMessageDisplay';
import { calcGrowth } from 'utils/calc';
import { ProductStyleBreakdownTable } from 'pages/trading/price/change/ProductStyleBreakdownTable';
import { PageRefTile } from 'components/Layout/Tiles/PageRefTile';
import { withSuppressedUrlPersistence } from 'hooks/state/UrlPersistence';
import { styleLink } from 'pages/entities/entityhelper';
import { useAppStore } from 'hooks/state/useAppStore';

export function PriceStyleDetailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearAll } = useAppStore();

  const { setBreadcrumbs, setDetailPageBreadcrumb } = useBreadcrumbStore();
  const { messageId, styleId } = useParams<{ messageId: string; styleId: string }>();

  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);

  const getAccountNumberParam = () => {
    const accountNumber = new URLSearchParams(location.search).get('accountNumber');
    return accountNumber ? `?accountNumber=${accountNumber}` : '';
  };

  const {
    data: message,
    isPending: messageIsPending,
    isError: messageIsError,
    error: messageError,
  } = useMessage(messageId);
  const {
    data: analysisData,
    isPending: analysisIsPending,
    isError: analysisIsError,
    error: analysisError,
  } = useAnalysisData(message?.analysisDataId);

  const styleData = useMemo(() => {
    if (!analysisData?.data) return [];
    const decodedTarget = decodeURIComponent(styleId ?? '');
    const [targetUniqueId, targetChannel] = decodedTarget.split('::');
    return (analysisData.data as PriceChange[]).filter(row => {
      const rowFullId = row.styNumber || row.prdNumber;
      if (targetChannel) {
        return rowFullId === targetUniqueId && (row.chnName || '') === targetChannel;
      }
      return rowFullId === targetUniqueId;
    });
  }, [analysisData, styleId]);

  const navState = location.state as { displayTitle?: string } | undefined;

  const { displayTitle, color, prdNumber } = useMemo(() => {
    const styleInfo = styleData[0];
    if (!styleInfo) return { color: '', displayTitle: '', prdNumber: '' };
    const color = styleInfo.color || (styleInfo.styNumber?.includes('|') ? styleInfo.styNumber.split('|')[1] : '');
    const name = styleInfo.prdName || styleId || '';
    return {
      color: color,
      displayTitle: navState?.displayTitle ?? `${name}${color ? ` | ${color}` : ''}`,
      prdNumber: styleData[0].prdNumber ?? '',
    };
  }, [navState, styleData, styleId]);

  useEffect(() => {
    if (message) {
      setDetailPageBreadcrumb(location.pathname, [
        { attribute: 'messageId', values: [messageId ?? ''] },
        { attribute: 'styleId', values: [displayTitle ?? ''] },
      ]);
    }
  }, [message, messageId, displayTitle, location.pathname, setDetailPageBreadcrumb]);

  const kpis = useMemo(() => {
    let totalOldGmv = 0,
      totalOldQty = 0;
    let totalNewGmv = 0,
      totalNewQty = 0;
    let priceDeltaSum = 0;

    styleData.forEach(row => {
      const oldStats = calculateDailyStats(row.fromPrice);
      const newStats = calculateDailyStats(row.toPrice);
      totalOldGmv += oldStats.dailyGmv;
      totalOldQty += oldStats.dailyQty;
      totalNewGmv += newStats.dailyGmv;
      totalNewQty += newStats.dailyQty;
      priceDeltaSum += calcGrowth(newStats.price, oldStats.price) * 100;
    });
    const count = styleData.length || 1;

    return {
      dailyQty: totalNewQty,
      dailyQtyDelta: calcGrowth(totalNewQty, totalOldQty) * 100,
      dailyGmv: totalNewGmv,
      dailyGmvDelta: calcGrowth(totalNewGmv, totalOldGmv) * 100,
      avgPriceChange: priceDeltaSum / count,
    };
  }, [styleData]);

  const filteredData = useMemo(
    () => (selectedArticleId ? styleData.filter(d => d.prdNumber === selectedArticleId) : styleData),
    [styleData, selectedArticleId]
  );

  const isPending = messageIsPending || analysisIsPending;
  const isError = messageIsError || analysisIsError;
  const errorDetails = getErrorMessage(messageError) ?? getErrorMessage(analysisError);

  const handleDetailsClick = () => {
    navigate(styleLink(prdNumber, color));
    withSuppressedUrlPersistence(() => {
      clearAll();
      setBreadcrumbs([]);
    });
  };

  const handleArticleVisibilityClick = () => {
    navigate(`/articlevisibility${getAccountNumberParam()}`);
    withSuppressedUrlPersistence(() => {
      clearAll();
      setBreadcrumbs([]);
    });
  };

  if (!isPending && !isError && !styleData.length) return <div className="p-10 text-center">Style data not found.</div>;

  return (
    <div className="standard-page-container">
      <PageContainer>
        <PageSectionTitle titleId={displayTitle} />
        {message && <TradingMessageDisplay message={message} showHeadline={true} />}
        {isPending ? (
          <Tile className="justify-center py-20">
            <LoadingSpinner />
          </Tile>
        ) : isError ? (
          <Tile errorDetails={errorDetails} testId="trading-price-style-error" />
        ) : (
          <>
            <PriceKPICard
              titleId="trading.price_change.kpi.avg_price_change"
              value={kpis.avgPriceChange / 100}
              unit={MeasureUnitCode.PERCENT}
              hidePill={true}
              subtextId="trading.price_change.kpi.subtext.avg_price_change"
            />
            <PriceKPICard
              titleId="trading.price_change.kpi.daily_items_new"
              value={kpis.dailyQty}
              unit={MeasureUnitCode.QUANTITY}
              delta={kpis.dailyQtyDelta}
              subtextId="trading.price_change.kpi.subtext.daily_items_old"
            />
            <PriceKPICard
              titleId="trading.price_change.kpi.daily_gmv_new"
              value={kpis.dailyGmv}
              unit={MeasureUnitCode.CURRENCY}
              delta={kpis.dailyGmvDelta}
              subtextId="trading.price_change.kpi.subtext.daily_gmv_old"
            />
            <StyleDetailChart
              data={filteredData}
              selectedArticleId={selectedArticleId}
              onClearFilter={() => setSelectedArticleId(null)}
            />
            <ProductStyleBreakdownTable data={styleData} />
          </>
        )}

        <PageSectionTitle
          titleId="general.title.explore_more"
          additionalTitle={<span className="text-2xl ml-[-10px]">...</span>}
        />
        <PageRefTile
          testId="tile-details"
          className="xl:col-span-6"
          iconName="Analytics"
          title={{ id: 'trading.drilldown.tile.details.style.title' }}
          description={{
            id: 'trading.drilldown.tile.details.description',
            values: { productName: displayTitle },
          }}
          buttonText={{ id: 'trading.drilldown.tile.details.button' }}
          onClick={handleDetailsClick}
        />

        <PageRefTile
          testId="tile-article-visibility"
          className="xl:col-span-6"
          iconName="ArticleVisibilityFilled"
          title={{ id: 'articlevisibility' }}
          description={{
            id: 'trading.drilldown.tile.articlevisibility.description',
            values: { productName: styleData[0].prdName },
          }}
          buttonText={{ id: 'trading.drilldown.tile.articlevisibility.button' }}
          onClick={handleArticleVisibilityClick}
        />
      </PageContainer>
    </div>
  );
}
